/* eslint-disable no-use-before-define, @typescript-eslint/no-non-null-assertion */
import { FC, useMemo, useState } from 'react';
import { CurrentRefinements, useHits } from 'react-instantsearch';
import { MultipleSelectFilter, PriceRangeFilter } from '../../../shared/sections/Filters/components';
import { useScrollDirection } from '../../../shared/use';
import { FoundHits, ResetFilters, useStats } from '../../../shared/components';
import { FilterIcon } from '../../../shared/sections/Filters/icons/Filter';
import { Filter } from '../../../shared/types';
import { currentPriceAttribute } from '../../../shared/utils';
import { GenericFilter } from '../../../shared/sections/Filters/components/GenericFilter';

import { useFeatureFlag } from '../../../shared/use/feature-flag';
import { CollectionFilter } from '../../../shared/sections/Filters/components/CollectionFilter';

type FiltersSectionProps = {
  baseFilters: Array<Filter>;
  mobile: boolean;
};

export const FiltersSection: FC<FiltersSectionProps> = ({ baseFilters, mobile }) => {
  const { results } = useHits();
  const showAvailabilityFilter = useFeatureFlag('showAvailabilityFilter');

  const newFacetAttributes = results?.renderingContent?.facetOrdering?.facets?.order || [];
  const newFilters = useMemo(() => getNewFilters(baseFilters, newFacetAttributes), [newFacetAttributes]);

  if (mobile) {
    return <FiltersSectionMobile baseFilters={baseFilters} newFilters={newFilters} />;
  }

  const availabilityFilter = baseFilters.find(({ attribute }) => attribute === 'availability')!;
  const vendorFilter = baseFilters.find(({ attribute }) => attribute === 'vendor')!;
  const priceFilter = baseFilters.find(({ attribute }) => attribute === 'price')!;

  return (
    <div className="sr-filters-container">
      <CollectionFilter />

      {newFilters.map((attribute, index) => (
        <GenericFilter key={attribute} sorterValue={index + 1} attribute={attribute} />
      ))}

      {showAvailabilityFilter && (
        <MultipleSelectFilter
          key={availabilityFilter.attribute}
          title={availabilityFilter.title}
          attribute={availabilityFilter.attribute}
          opened={availabilityFilter.opened}
        />
      )}

      <MultipleSelectFilter
        key={vendorFilter.attribute}
        title={vendorFilter.title}
        attribute={vendorFilter.attribute}
        opened={vendorFilter.opened}
      />

      <PriceRangeFilter
        key={currentPriceAttribute}
        title={priceFilter.title}
        attribute={currentPriceAttribute}
        opened={priceFilter.opened}
      />
    </div>
  );
};

const getNewFilters = (baseFilters: Array<Filter>, newFacetAttributes: Array<string>): Array<string> => {
  const baseAttributes = baseFilters.map(({ attribute }) => attribute);

  const newAttributesWithoutBase = newFacetAttributes.filter((attribute) => !baseAttributes.includes(attribute));

  return newAttributesWithoutBase;
};

type FiltersSectionMobileProps = {
  baseFilters: Array<Filter>;
  newFilters: Array<string>;
};

export const FiltersSectionMobile: FC<FiltersSectionMobileProps> = ({ baseFilters, newFilters }) => {
  const scrollDirection = useScrollDirection();
  const [showFilters, setShowFilters] = useState(false);
  const { nbHits } = useStats();

  const vendorFilter = baseFilters.find(({ attribute }) => attribute === 'vendor')!;
  const priceFilter = baseFilters.find(({ attribute }) => attribute === 'price')!;

  return (
    <>
      <div
        className="sr-mb-filters-container"
        style={{ top: scrollDirection === 'down' || showFilters ? 0 : 'var(--top-header-height, 85px)' }}
      >
        <FoundHits />

        <div>
          <button className="filter-button" onClick={() => setShowFilters(!showFilters)}>
            <FilterIcon />
            {theme.locales.filterLabel}
          </button>
        </div>

        {/* current refinements should be exist in DOM so that user can reset them */}
        <CurrentRefinements style={{ display: 'none' }} />

        <ResetFilters />
      </div>

      <div style={{ display: showFilters ? 'block' : 'none' }}>
        <div className="sr-mb-filters-wrapper" style={{ top: '35px' }}>
          <CollectionFilter />

          {newFilters.map((attribute, index) => (
            <GenericFilter key={attribute} sorterValue={index + 1} attribute={attribute} />
          ))}

          <MultipleSelectFilter
            key={vendorFilter.attribute}
            title={vendorFilter.title}
            attribute={vendorFilter.attribute}
            opened={vendorFilter.opened}
          />

          <PriceRangeFilter
            key={priceFilter.attribute}
            title={priceFilter.title}
            attribute={priceFilter.attribute}
            opened={priceFilter.opened}
          />
        </div>

        <div className="sr-mb-filters-footer">
          <ResetFilters containerClassName="reset-filters-button" />

          <button className="see-results-button" onClick={() => setShowFilters(false)}>
            {theme.locales.seeAmountOfResult.replace('[amount]', Number(nbHits).toLocaleString())}
          </button>
        </div>
      </div>
    </>
  );
};
